// TMOS Historical Trends Page
// Date: January 13, 2026
// Purpose: Display historical trends for objectives

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { businessPerformanceService } from '@/services/businessPerformanceService';
import { TrendDataPoint } from '@/types/businessPerformance';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ChartBarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

export const HistoricalTrendsPage: React.FC = () => {
  const { objectiveId } = useParams<{ objectiveId: string }>();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan_id');
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [trendData, setTrendData] = useState<TrendDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState<number>(12);

  useEffect(() => {
    if (objectiveId) {
      loadTrendData();
    }
  }, [objectiveId, planId, months]);

  const loadTrendData = async () => {
    if (!objectiveId) return;
    try {
      setLoading(true);
      const data = await businessPerformanceService.getHistoricalTrends(objectiveId, planId || undefined, months);
      setTrendData(data);
    } catch (error: any) {
      console.error('Error loading historical trends:', error);
      toast.error(error.message || t('businessPerformance.errorLoadingTrends'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)]">
        <div className="container mx-auto px-2 py-2">
          <div className="flex items-center justify-center h-64">
            <div className="text-[var(--color-secondary-text)]">Loading trends...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(objectiveId ? `/performance/objectives/${objectiveId}${planId ? `?plan_id=${planId}` : ''}` : '/performance')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {t('common.back') || 'Back'}
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">
                {t('businessPerformance.historicalTrends') || 'Historical Trends'}
              </h1>
              <p className="text-[var(--color-secondary-text)]">
                {t('businessPerformance.historicalTrendsDescription') || 'View historical performance data over time'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-[var(--color-secondary-text)]">
                {t('businessPerformance.months') || 'Months'}:
              </label>
              <select
                value={months}
                onChange={(e) => setMonths(parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-[var(--color-surface-text)]"
              >
                <option value="6">6 {t('common.months') || 'months'}</option>
                <option value="12">12 {t('common.months') || 'months'}</option>
                <option value="24">24 {t('common.months') || 'months'}</option>
                <option value="36">36 {t('common.months') || 'months'}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Chart Placeholder */}
        <Card variant="outline" className="mb-6">
          <CardContent className="p-4">
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
              {t('businessPerformance.trendChart') || 'Trend Chart'}
            </h2>
            <div className="h-96 flex items-center justify-center bg-gray-50 rounded">
              <div className="text-center">
                <ChartBarIcon className="w-16 h-16 text-[var(--color-secondary-text)] mx-auto mb-4 opacity-50" />
                <p className="text-sm text-[var(--color-secondary-text)] mb-2">
                  {t('businessPerformance.chartPlaceholder') || 'Chart visualization will be implemented here'}
                </p>
                <p className="text-xs text-[var(--color-secondary-text)]">
                  {trendData.length} {t('businessPerformance.dataPoints') || 'data points'} ({months} {t('common.months') || 'months'})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        {trendData.length > 0 ? (
          <Card variant="outline">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.date') || 'Date'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.target') || 'Target'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.actual') || 'Actual'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('businessPerformance.variance') || 'Variance'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trendData.map((point, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {new Date(point.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {point.target_value} {point.unit || ''}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {point.actual_value !== null ? `${point.actual_value.toFixed(2)} ${point.unit || ''}` : t('businessPerformance.noData') || 'No Data'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={point.variance >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {point.variance >= 0 ? '+' : ''}{point.variance.toFixed(2)} {point.unit || ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outline">
            <CardContent className="p-8 text-center">
              <CalendarIcon className="w-12 h-12 text-[var(--color-secondary-text)] mx-auto mb-4 opacity-50" />
              <p className="text-[var(--color-secondary-text)]">
                {t('businessPerformance.noTrendData') || 'No trend data available for this objective.'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

